import { AnchorButton, Badge, ButtonAsLink, Link, Anchor } from '@cluey/cluey-components';
import env from '../../../../env';
import ChangeSessionModal from '../../../Modals/ChangeSessionModal/ChangeSessionModal';
import ManageUpcomingSessionModal from '../../../Modals/ManageUpcomingSessionModal/ManageUpcomingSessionModal';
import moment from 'moment';
import { isUndefined, noop } from 'lodash';
import { sessionStartsInLessThanADay } from '../../../../util/helper/sessions';
import { ReactNode, useState } from 'react';
import { formatSession } from '../../../../util/helpers';
import { getPkgTutorNameByEnrolmentId } from '../../../../selectors/hubEnrolmentsSelector';
import {
  TTBM_CUSTOMER_NUMBER,
  DATE_FORMAT_TYPE_13,
  STUDENT_PROGRESS_URL_PARAM_TAB,
  STUDENT_PROGRESS_TAB_VIEWS,
} from '../../../../util/constants';
import { ListItemHeader } from './ListItemHeader';
import { SessionDetails } from './SessionDetails';
import SessionPaymentInfo from '../SessionPaymentInfo';
import { useLiveUpdateSessionStatus } from './hooks';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { DesktopOnly } from '../../../DesktopOnly/DesktopOnly';
import { PATH_HUB_RESCHEDULE_SESSION_WITH_TUTOR, PATH_NAME_REPORT } from '../../../../util/pagePath';
import { generatePath } from 'react-router';
import type { Session } from '../../../../types/hubInterfaces';
import isSessionJoinable from '../../../../util/helper/isSessionJoinable';
import { usePostHogCapture } from '../../../../util/posthog';
import courseIcon from '../../../../assets/images/icon-filled-session-course.svg';
import classroomIcon from '../../../../assets/images/icon-filled-session-masterclass.svg';
import alertIcon from '../../../../assets/images/icon-filled-alert-exclamation.svg';
import { api } from '../../../../api';
import { InlineLoader } from '../../../../common/InlineLoader';
import { getSpecificActiveEnrolment } from '../../../../selectors/enrolmentSelector';

interface Props {
  session: Session;
  timezone: string;
  isPreview?: boolean;
}

const HubSessionsListItem = ({ session, timezone, isPreview = false }: Props) => {
  const {
    student,
    subject,
    tutor,
    tutorNumber,
    uniqueEnrolmentId,
    paymentStatus,
    studentSessionId,
    tutorRescheduleRequestStatusC,
    sessionReportId,
    type,
    isFull,
    conflictingSession,
  } = session;
  const isMaintenanceMode = env.REACT_APP_MAINTENANCE_MODE;
  const isTodaySession = moment(session.startTime).isSame(new Date(), 'day');
  const showJoinButton = isSessionJoinable(session);
  const isClassroomSession = type === 'Classroom';
  const [changeSessionModalOpen, setChangeSessionModalOpen] = useState(false);
  const [classroomIsFull, setClassroomIsFull] = useState(isFull);
  const enrolment = useSelector(getSpecificActiveEnrolment({ enrolmentId: uniqueEnrolmentId }));

  const capture = usePostHogCapture();

  const sessionProgress = useLiveUpdateSessionStatus({
    startTime: session.startTime,
  });

  const { mutate: joinClassroom, isLoading: generatingClassroomLink } = api.classroom.joinClassroom.useMutation({
    onSuccess(data) {
      const { classroomLink, studentToken } = data;
      window.open(`${classroomLink}/latest/?token=${studentToken}`, '_blank');
    },
    onError() {
      setClassroomIsFull(true);
    },
  });

  const closeChangeSessionModal = () => {
    setChangeSessionModalOpen(false);
  };
  const openChangeSessionModal = () => {
    capture('click_session-manage');
    setChangeSessionModalOpen(true);
  };
  const getBoxBackgroundColor = () => {
    if (paymentStatus === 'Failed') {
      return 'bg-red-1';
    }
    if (tutorRescheduleRequestStatusC === 'Requested') {
      return 'bg-yellow-1';
    }
    if (showJoinButton) {
      return 'bg-blue-1';
    }
    return 'bg-white';
  };

  let badge: ReactNode | null = null;
  if (session.tutorRescheduleRequestStatusC === 'Approved') {
    badge = (
      <Badge className="flex min-h-[20px] flex-shrink-0 items-center rounded-xl !bg-slate-3 py-px !font-bold">
        Rescheduled with Tutor
      </Badge>
    );
  }

  if (session.tutorRescheduleRequestStatusC === 'Requested') {
    badge = (
      <Badge className="flex h-[20px] flex-shrink-0 items-center rounded-xl py-px !font-bold">
        Tutor Can't Make It
      </Badge>
    );
  }

  if (session.enrolmentType === 'Trial') {
    badge = (
      <Badge className="flex h-[20px] flex-shrink-0 items-center rounded-xl !bg-slate-3 py-px !font-bold">Trial</Badge>
    );
  }

  let sessionType: string = '';
  let sessionIcon: string = classroomIcon;
  let timerSpacingClass: string = '';
  switch (type) {
    case 'Classroom':
      sessionType = 'Masterclass';
      timerSpacingClass = 'left-36';
      break;
    case 'Group':
      sessionType = 'Group';
      timerSpacingClass = 'left-[6.5rem]';
      break;
    case 'Program':
      sessionType = 'Program';
      timerSpacingClass = 'left-[7.5rem]';
      break;
    default:
      sessionType = '1-on-1';
      sessionIcon = courseIcon;
      timerSpacingClass = 'left-[6.5rem]';
      break;
  }

  const packageTutor = useSelector((state) => getPkgTutorNameByEnrolmentId(state, uniqueEnrolmentId));
  const displayTutor = tutorNumber === TTBM_CUSTOMER_NUMBER ? packageTutor : tutor;
  const { date, time } = formatSession({
    session,
    timezone,
    sessionDateFormat: DATE_FORMAT_TYPE_13,
    useSemiColon: false,
  });
  const isRescheduleRequested = session.tutorRescheduleRequestStatusC === 'Requested';

  const renderManageOptions = (className?: string) => (
    <>
      {!isMaintenanceMode && !isClassroomSession && (
        <div className={classNames('flex flex-col items-start gap-1 lg:items-end', className)}>
          <ButtonAsLink onClick={isPreview ? noop : openChangeSessionModal} size="base">
            {isTodaySession ? "Can't make it" : 'Manage'}
          </ButtonAsLink>
        </div>
      )}
      {isClassroomSession && (
        <div className={classNames('flex flex-col items-start gap-1 lg:items-end', className)}>
          <span>
            <Anchor
              href={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.SESSION_REPORT}`}
              target="_blank"
              size="base"
              className="underline underline-offset-4"
            >
              View student progress
            </Anchor>
          </span>
          <span>
            <Anchor
              href={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.ASSESSMENTS}`}
              target="_blank"
              size="base"
              className="underline underline-offset-4"
            >
              View practice questions
            </Anchor>
          </span>
          <span>
            <Anchor
              href={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.PROGRESS_OVERVIEW}`}
              target="_blank"
              size="base"
              className="underline underline-offset-4"
            >
              View upcoming topics
            </Anchor>
          </span>
          {/* TODO: Post MVP: Implement manage subscription */}
          {/* <span>Manage subscription</span> */}
        </div>
      )}
    </>
  );

  return (
    <div
      data-testid="session-list-item"
      className={classNames(
        'relative flex w-full flex-col items-start gap-1 rounded-lg border-2 border-grey-2 px-5 py-3 shadow-md xl:flex',
        getBoxBackgroundColor()
      )}
    >
      {sessionStartsInLessThanADay({ session, timezone }) ? (
        <ManageUpcomingSessionModal
          open={changeSessionModalOpen}
          closeModal={closeChangeSessionModal}
          session={session}
        />
      ) : (
        <ChangeSessionModal
          open={changeSessionModalOpen}
          closeModal={closeChangeSessionModal}
          session={session}
          timezone={timezone}
        />
      )}
      <div className="absolute -left-[0.5px] -top-[0.5px] rounded-br-lg rounded-tl-lg bg-slate-4 text-base font-bold">
        <div className="flex flex-row items-center gap-x-1 px-3 py-2">
          <img src={sessionIcon} className="h-5 w-5" alt="session icon" />
          <span className="text-white">{sessionType}</span>
        </div>
      </div>
      <div
        className={classNames(
          'absolute top-2 flex flex-row items-center gap-2 text-xs font-bold uppercase',
          timerSpacingClass
        )}
      >
        <ListItemHeader session={session} sessionStatus={sessionProgress} />
      </div>
      <div className="flex w-full flex-col lg:flex-row">
        <div className="mt-9 flex w-full grow basis-2/3 flex-col items-start justify-between gap-2">
          <div className="flex flex-col">
            <div className="flex flex-col flex-wrap items-start gap-2 lg:flex-row lg:items-center lg:gap-3">
              <SessionDetails student={student} subject={subject} tutor={displayTutor} />
              {badge}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {isRescheduleRequested && (
              <Link
                to={generatePath(PATH_HUB_RESCHEDULE_SESSION_WITH_TUTOR, {
                  sessionId: studentSessionId,
                })}
                size="base"
              >
                Reschedule with {tutor}
              </Link>
            )}
            <span className="text-xs font-bold uppercase">{isTodaySession ? 'Today' : date}</span>
            <span className="text-base">{time}</span>
            <p className="block text-base text-grey-6 lg:hidden">{sessionProgress}</p>
            {renderManageOptions('flex lg:hidden')}
            {isClassroomSession && !isUndefined(conflictingSession) && (
              <div className="flex flex-row items-start gap-x-2 pt-2 lg:items-center lg:pt-0">
                <img src={alertIcon} className="h-5 w-5" alt="alert icon" />
                <span className="font-bold">
                  This class clashes with your{' '}
                  {conflictingSession.type === 'Course' ? '1-on-1' : conflictingSession.type}{' '}
                  {conflictingSession.subject} session (with {conflictingSession.tutor})
                </span>
              </div>
            )}
          </div>
          {!isMaintenanceMode && !isClassroomSession && <SessionPaymentInfo session={session} />}
        </div>
        <div className="hidden w-full basis-1/3 flex-col items-end gap-1 lg:flex">
          <div>
            {showJoinButton && paymentStatus !== 'Failed' && !classroomIsFull ? (
              <AnchorButton
                size="sm"
                href={!isPreview && !isClassroomSession ? session.sessionToken : undefined}
                onClick={() => {
                  capture('click_session-join');
                  if (isClassroomSession) {
                    joinClassroom({
                      sessionId: session.sessionToken,
                      enrolment,
                    });
                  }
                }}
                className="mb-1 hidden min-w-[80px] !px-6 normal-case lg:inline-flex lg:items-center lg:justify-center"
              >
                {generatingClassroomLink && <InlineLoader />}
                <span className={classNames(generatingClassroomLink && '!text-transparent')}>Join now</span>
              </AnchorButton>
            ) : (
              <div className="flex flex-col items-start gap-1 lg:items-end">
                {classroomIsFull ? (
                  <p className="proxima-soft font-bold" style={{ color: '#DA392B' }}>
                    Class is full
                  </p>
                ) : (
                  <p className="text-base text-grey-6 lg:block">{sessionProgress}</p>
                )}
              </div>
            )}
          </div>
          <div>{renderManageOptions()}</div>
        </div>
      </div>
      <DesktopOnly className={classNames(showJoinButton ? 'flex' : 'hidden')} />
    </div>
  );
};

export default HubSessionsListItem;
